import { AUDIENCES, type Audience, audienceLabel } from "~/features/audience"
import type { AccessState } from "~/features/entitlements/types"

/**
 * The page-level filter row, matching MLC's `Filter by: [content type]` plus
 * Search (`components/DigitalMediaUI.js`), with one addition: a status filter.
 *
 * **Status is a filter and not a tab set, deliberately.** Tabs across
 * Active / Upcoming / Expired would multiply against the seven content sections
 * the page already has, and they lead with a distinction that is uninteresting on
 * almost every row — 65 of the 68 seats on the UAT account have no end date at
 * all, so an "Active" tab would hold everything and the other two would look
 * broken. A filter defaults to showing the lot and lets someone ask the narrow
 * question (`what have I lost access to?`) when they have it.
 */

export type ContentTypeFilter =
  | "all"
  | "studies"
  | "events"
  | "ebooks"
  | "downloads"
  | "other-content"
  | "memberships"
  | "other"

export type StatusFilter = "all" | AccessState

export type AudienceFilter = "all" | Audience

export interface LibraryFilters {
  readonly contentType: ContentTypeFilter
  readonly status: StatusFilter
  readonly audience: AudienceFilter
  readonly searchTerm: string
}

export const DEFAULT_FILTERS: LibraryFilters = {
  contentType: "all",
  status: "all",
  audience: "all",
  searchTerm: "",
}

/** Section slugs, in page order. The values double as the content-type filter. */
export const CONTENT_TYPE_OPTIONS: readonly { readonly value: ContentTypeFilter; readonly label: string }[] = [
  { value: "all", label: "Show all content types" },
  { value: "studies", label: "Studies" },
  { value: "events", label: "Events/Simulcasts" },
  { value: "ebooks", label: "eBooks" },
  { value: "downloads", label: "Downloads" },
  { value: "memberships", label: "Memberships" },
  { value: "other-content", label: "All other content" },
  { value: "other", label: "Other" },
]

export const STATUS_OPTIONS: readonly { readonly value: StatusFilter; readonly label: string }[] = [
  { value: "all", label: "Any status" },
  { value: "active", label: "Available now" },
  { value: "upcoming", label: "Available soon" },
  { value: "expired", label: "Expired" },
]

export const AUDIENCE_OPTIONS: readonly { readonly value: AudienceFilter; readonly label: string }[] = [
  { value: "all", label: "All audiences" },
  ...AUDIENCES.map(value => ({ value, label: audienceLabel(value) })),
]

export const matchesAudience = (filters: LibraryFilters, audience: Audience): boolean =>
  filters.audience === "all" || filters.audience === audience

export const isSectionVisible = (filters: LibraryFilters, sectionId: string): boolean =>
  filters.contentType === "all" || filters.contentType === sectionId

export const matchesSearch = (filters: LibraryFilters, title: string): boolean => {
  const term = filters.searchTerm.trim().toLowerCase()

  return term.length === 0 || title.toLowerCase().includes(term)
}

/**
 * A row with no known window counts as active — which is what an absent seat
 * means everywhere else on this page (a null `viewUntil` is unbounded, not
 * expired). So the Expired filter narrows to rows we have positive evidence
 * about, and never hides something on a guess.
 */
export const matchesStatus = (filters: LibraryFilters, state: AccessState | undefined): boolean =>
  filters.status === "all" || filters.status === (state ?? "active")

export const isFiltering = (filters: LibraryFilters): boolean =>
  filters.contentType !== "all" ||
  filters.status !== "all" ||
  filters.audience !== "all" ||
  filters.searchTerm.trim().length > 0
